import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import './App.css';
import './index.css';
import Header from './ui/components/Header';
import Footer from './ui/components/Footer';
import TermsPage from './ui/pages/TermsPage';
import ResetPasswordPage from './ui/pages/ResetPasswordPage';
import ForgotPasswordPage from './ui/pages/ForgotPasswordPage';
import ConfirmationPage from './ui/pages/ConfirmationPage';
import AuthLoginPage from './ui/pages/AuthLoginPage';
import AuthCallbackPage from './ui/pages/AuthCallbackPage';
import LandingPage from './ui/pages/LandingPage';
import WaitlistLandingPage from './ui/pages/WaitlistLandingPage';
import OpenInAppPage from './ui/pages/OpenInAppPage';
import DeleteAccountPage from './ui/pages/DeleteAccountPage';
import MyCommunityPage from './ui/pages/MyCommunityPage';
import LeaderboardPage from './ui/pages/LeaderboardPage';
import CountryLeaderboardPage from './ui/pages/CountryLeaderboardPage';
import CityLeaderboardPage from './ui/pages/CityLeaderboardPage';
import ReferralLandingPage from './ui/pages/ReferralLandingPage';
import RequireAuth from './ui/components/RequireAuth';
import { ApiContextProvider } from './application/services/ApiContextService';

// Pages that have their own header/footer — no global chrome. /leaderboard and /i join this list
// (not the Header/Footer app shell below) because both are purpose-designed, public, shareable
// pages (Web UI section) with their own distinct visual identity — the generic app Header/Footer
// is tuned for the rest of the site, not this feature's gamification-driven surface.
// /landing-legacy needs its own entry: the isStandalone prefix check appends '/', so '/landing'
// does not cover it.
const STANDALONE_PREFIXES = ['/', '/landing', '/landing-legacy', '/auth', '/terms', '/privacy', '/community', '/content-policy', '/cookies', '/copyright', '/child-safety', '/resetPassword', '/forgot-password', '/confirmation', '/events', '/match', '/profile', '/delete-account', '/leaderboard', '/i'];

const isStandalone = (pathname: string) =>
  STANDALONE_PREFIXES.some(p => pathname === p || pathname.startsWith(p + '/'));

const AppContent: React.FC = () => {
  const location = useLocation();

  const UnderConstruction: React.FC<{ pageName: string }> = ({ pageName }) => (
    <div className="under-construction">
      <h1>{pageName} Sayfası</h1>
      <p>Bu sayfa şu anda yapım aşamasındadır.</p>
    </div>
  );

  const toast = (
    <ToastContainer
      position="top-right"
      autoClose={5000}
      hideProgressBar={false}
      newestOnTop
      closeOnClick
      rtl={false}
      pauseOnFocusLoss
      draggable
      pauseOnHover
    />
  );

  if (isStandalone(location.pathname)) {
    return (
      <>
        <Routes>
          {/* Waitlist retired 2026-08-18 — normal LandingPage is now live; WaitlistLandingPage
              kept at /landing-legacy in case it's needed again, not linked anywhere. */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/landing" element={<LandingPage />} />
          <Route path="/landing-legacy" element={<WaitlistLandingPage />} />

          {/* Auth */}
          <Route path="/auth/login" element={<AuthLoginPage />} />
          {/* New web login session (D9) — separate OAuth2/PKCE flow, does not touch /auth/login */}
          <Route path="/auth/callback" element={<AuthCallbackPage />} />

          {/* Legal */}
          <Route path="/terms" element={<TermsPage defaultTab="terms" />} />
          <Route path="/privacy" element={<TermsPage defaultTab="privacy" />} />
          <Route path="/community" element={<TermsPage defaultTab="communityGuidelines" />} />
          <Route path="/content-policy" element={<TermsPage defaultTab="contentPolicy" />} />
          <Route path="/cookies" element={<TermsPage defaultTab="cookies" />} />
          <Route path="/copyright" element={<TermsPage defaultTab="copyright" />} />
          <Route path="/child-safety" element={<TermsPage defaultTab="childSafety" />} />

          {/* Account flows */}
          <Route path="/resetPassword/:token" element={<ResetPasswordPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/confirmation/:confirmationId" element={<ConfirmationPage />} />
          <Route path="/delete-account" element={<DeleteAccountPage />} />

          {/* Public leaderboards (Slice 3, referral-leaderboard-program.md) — no auth, shareable */}
          <Route path="/leaderboard" element={<LeaderboardPage />} />
          <Route path="/leaderboard/:countryCode" element={<CountryLeaderboardPage />} />
          <Route path="/leaderboard/:countryCode/:citySlug" element={<CityLeaderboardPage />} />

          {/* Referral landing (D2) — deep-links into the app, stores the code */}
          <Route path="/i/:code" element={<ReferralLandingPage />} />
        </Routes>
        {toast}
      </>
    );
  }

  return (
    <div className="App">
      <Header />
      <div className="app-container">
        <Routes>
          <Route path="/about" element={<UnderConstruction pageName="Hakkımızda" />} />
          <Route path="/careers" element={<UnderConstruction pageName="Kariyer" />} />
          <Route path="/advertise" element={<UnderConstruction pageName="Reklam" />} />
          <Route path="/help" element={<UnderConstruction pageName="Yardım Merkezi" />} />
          <Route path="/signup" element={<UnderConstruction pageName="Kaydol" />} />
          <Route path="/honor-code" element={<UnderConstruction pageName="Onur Kodu" />} />
          <Route path="/privacy-page" element={<UnderConstruction pageName="Gizlilik" />} />
          <Route path="/schools" element={<UnderConstruction pageName="Okullar" />} />
          <Route path="/parents" element={<UnderConstruction pageName="Ebeveynler" />} />

          {/* Email/notification deep-links → bounce into the mobile app via getspeakhub:// scheme */}
          <Route path="/events/:id" element={<OpenInAppPage screen="events" />} />
          <Route path="/match/search" element={<OpenInAppPage screen="match/search" />} />
          <Route path="/profile" element={<OpenInAppPage screen="profile" />} />

          {/* "Your Community" dashboard (D6/D9) — not /community, which is the legal page above */}
          <Route
            path="/my-community"
            element={
              <RequireAuth>
                <MyCommunityPage />
              </RequireAuth>
            }
          />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
      <Footer />
      {toast}
    </div>
  );
};

const App: React.FC = () => {
  return (
    <ApiContextProvider>
      <Router>
        <AppContent />
      </Router>
    </ApiContextProvider>
  );
};

export default App;
